import { isDefiniteShopRejection } from "./shopMutationOutcome";
import React, { useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, TextField } from "@mui/material";
import { ShopApiError, shopAdminApi } from "./shopAdminApi";
import { validWebshopPrice, validWebshopVat } from "./offerInput";
import { isShopOfferSettings, shopOfferResultMatches, type ShopOfferSettings, type ShopOfferIntent } from "./shopOfferModel";

export default function ShopOfferEditor({ user, packageDocId, intent, intentVersion, onIntentSettled, onClose, onDenied, onSaved }: {
  user: User; packageDocId: string; intent: React.MutableRefObject<ShopOfferIntent | null>; intentVersion: number;
  onIntentSettled: () => void; onClose: () => void; onDenied: () => void; onSaved: () => void;
}) {
  const original = intent.current;
  const [snapshot, setSnapshot] = useState<ShopOfferSettings | null>(null);
  const [price, setPrice] = useState(original?.body.priceWebshop == null ? "" : String(original.body.priceWebshop));
  const [vat, setVat] = useState(original?.body.vatRate == null ? "" : String(original.body.vatRate));
  const [shopEnabled, setShopEnabled] = useState(original?.body.shopEnabled ?? true);
  const [reason, setReason] = useState(original?.body.reason || "");
  const [pending, setPending] = useState(!!original);
  const [saving, setSaving] = useState(!!original?.inFlight);
  const [reading, setReading] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [error, setError] = useState(original ? "Sparandet kunde inte bekräftas. Försök igen med samma uppgifter." : "");
  const live = useRef(true), busy = useRef(false), readId = useRef(0);
  const load = async () => {
    if (busy.current || intent.current) return;
    busy.current = true; const current = ++readId.current;
    setReading(true); setSnapshot(null); setReason(""); setError(""); setRejected(false);
    try {
      const fresh = await shopAdminApi.offer(user, packageDocId);
      if (!live.current || current !== readId.current) return;
      if (!isShopOfferSettings(fresh) || fresh.packageDocId !== packageDocId) throw new ShopApiError(502, "invalid_response");
      setSnapshot(fresh); setPrice(fresh.priceWebshop === null ? "" : String(fresh.priceWebshop));
      setVat(fresh.vatRate === null ? "" : String(fresh.vatRate)); setShopEnabled(fresh.shopEnabled);
    } catch (caught) {
      if (!live.current || current !== readId.current) return;
      if (caught instanceof ShopApiError && [401, 403].includes(caught.status)) onDenied();
      else setError("Aktuell förpackning kunde inte hämtas. Hämta igen innan du ändrar pris eller moms.");
    } finally { if (current === readId.current) { busy.current = false; if (live.current) setReading(false); } }
  };
  useEffect(() => {
    live.current = true;
    if (!intent.current) void load();
    return () => { live.current = false; if (!intent.current?.inFlight) busy.current = false; };
    // Parent keys this editor to the current user/capability/package context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    setPending(!!intent.current); setSaving(!!intent.current?.inFlight);
    if (pending && !intent.current && !rejected) { setRejected(true); setSnapshot(null);
      setError("Den tidigare ändringen har avslutats. Hämta aktuell förpackning och granska igen."); }
    // A settlement signal never hydrates data from a former context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intentVersion]);
  const save = async () => {
    if (busy.current || intent.current?.inFlight || rejected || reading) return;
    if (!intent.current) {
      if (!snapshot || snapshot.revision >= Number.MAX_SAFE_INTEGER - 1 || !reason.trim() || reason.trim().length > 500) return;
      const parsedPrice = price.trim() ? Number(price.replace(",", ".")) : null;
      const parsedVat = vat.trim() ? Number(vat.replace(",", ".")) : null;
      if ((parsedPrice !== null && !validWebshopPrice(parsedPrice)) || (parsedVat !== null && !validWebshopVat(parsedVat)) ||
          (parsedPrice !== null && parsedVat === null)) {
        setError("Priset måste avrundas till minst ett öre och vara högst 1 000 000 kr. Momssatsen ska vara mellan 0 och 100 % med högst två decimaler. Pris kräver moms."); return;
      }
      intent.current = { uid: user.uid, id: packageDocId, body: { priceWebshop: parsedPrice, vatRate: parsedVat, shopEnabled,
        expectedRevision: snapshot.revision, reason: reason.trim() }, key: crypto.randomUUID(), inFlight: false };
    }
    const attempt = intent.current;
    if (attempt.uid !== user.uid || attempt.id !== packageDocId) return;
    attempt.inFlight = true; busy.current = true; setSaving(true); setPending(true); setError("");
    onIntentSettled();
    try {
      const result = await shopAdminApi.updateOffer(user, attempt.id, attempt.body, attempt.key);
      if (!isShopOfferSettings(result) || !shopOfferResultMatches(result, attempt.id, attempt.body)) throw new ShopApiError(502, "invalid_response");
      if (intent.current === attempt) intent.current = null;
      if (!live.current) return;
      live.current = false; onSaved(); onClose();
    } catch (caught) {
      const definitive = isDefiniteShopRejection(caught, attempt.uncertain);
      if (!definitive) attempt.uncertain = true;
      if (definitive && intent.current === attempt) intent.current = null;
      if (!live.current) return;
      if (caught instanceof ShopApiError && [401, 403].includes(caught.status)) onDenied();
      else if (definitive) { setPending(false); setRejected(true); setSnapshot(null);
        setError("Ändringen avvisades. Hämta aktuell förpackning och granska igen med en ny orsak."); }
      else setError("Sparandet kunde inte bekräftas. Försök igen med samma uppgifter.");
    } finally { attempt.inFlight = false; busy.current = false; if (live.current) setSaving(false); onIntentSettled(); }
  };
  const close = () => { if (busy.current || pending) return; live.current = false; readId.current++; onClose(); };
  const frozen = reading || saving || pending || rejected || !snapshot;
  return <Dialog open onClose={close} fullWidth maxWidth="sm">
    <DialogTitle>Webbshoppris och moms</DialogTitle>
    <DialogContent className="WebshopOfferDialog">
      {reading && <div role="status"><CircularProgress size={24} /> Hämtar aktuell förpackning…</div>}
      {error && <Alert severity="error">{error}</Alert>}
      {snapshot && <p>Revision {snapshot.revision}. Pris per försäljningsförpackning i SEK inklusive moms.</p>}
      {(snapshot || pending) && <><TextField label="Pris inkl. moms (kr)" value={price} onChange={(event) => setPrice(event.target.value)} disabled={frozen} fullWidth size="small" inputProps={{ inputMode: "decimal" }} helperText="Avrundas till öre i kassan. Tomt fält tar bort priset." />
        <TextField label="Momssats (%)" value={vat} onChange={(event) => setVat(event.target.value)} disabled={frozen} fullWidth size="small" inputProps={{ inputMode: "decimal" }} helperText="Ange granskad momssats med högst två decimaler. Noll är en sparad momssats." />
        <FormControlLabel control={<Switch checked={shopEnabled} onChange={(event) => setShopEnabled(event.target.checked)} disabled={frozen} />} label="Erbjud förpackningen i webbshoppen" />
        <TextField label="Orsak" value={reason} onChange={(event) => setReason(event.target.value)} disabled={frozen} fullWidth size="small" required inputProps={{ maxLength: 500 }} />
      </>}
      {pending && <Alert severity="warning">En ändring är obekräftad. Endast samma uppgifter kan skickas igen. Efter fullständig omladdning måste aktuella uppgifter granskas före en ny ändring.</Alert>}
      <p>Ändringen sparas på den befintliga förpackningen. Försäljning är fortsatt avstängd tills hela köpflödet är godkänt.</p>
    </DialogContent>
    <DialogActions sx={{ flexWrap: "wrap", gap: 1, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
      <Button onClick={close} disabled={reading || saving || pending}>Avbryt</Button>
      {!pending && <Button onClick={load} disabled={reading || saving}>Hämta aktuell förpackning</Button>}
      <Button onClick={save} disabled={reading || saving || rejected || (!pending && (!snapshot || !reason.trim() || snapshot.revision >= Number.MAX_SAFE_INTEGER - 1))}>
        {pending ? "Försök igen med samma uppgifter" : "Spara"}</Button>
    </DialogActions>
  </Dialog>;
}
